import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import SourceDataService from "src/services/sourceDataService";
import { Incident, DragonflyData, NearmissData } from "src/types/sourceDataTypes";

export type SourceDataState = {
  incident: Incident | null;
  dragonfly: DragonflyData | null;
  nearmiss: NearmissData | null;
  getIncidentLoading: LOADING_STATE;
  getDragonflyLoading: LOADING_STATE;
  getNearmissLoading: LOADING_STATE;
};

const initialState: SourceDataState = {
  incident: null,
  dragonfly: null,
  nearmiss: null,
  getIncidentLoading: "idle",
  getDragonflyLoading: "idle",
  getNearmissLoading: "idle",
};

export const getSourceDataIncident = createAsyncThunk(
  "sourceData/getIncident",
  async (incidentId: string) => {
    const { data } = await SourceDataService.getIncident(incidentId);
    return data;
  },
);

export const getSourceDataDragonfly = createAsyncThunk(
  "sourceData/getDragonfly",
  async (findingId: string) => {
    const { data } = await SourceDataService.getDragonfly(findingId);
    return data;
  },
);

export const getSourceDataNearmiss = createAsyncThunk(
  "sourceData/getNearmiss",
  async (nearmissId: string) => {
    const { data } = await SourceDataService.getNearmiss(nearmissId);
    return data;
  },
);

const { reducer, actions } = createSlice({
  name: "sourceDataSlice",
  initialState,
  reducers: {
    resetSourceData: (state) => {
      state.incident = null;
      state.dragonfly = null;
      state.nearmiss = null;
      state.getIncidentLoading = "idle";
      state.getDragonflyLoading = "idle";
      state.getNearmissLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    // Incident data reducers
    builder.addCase(getSourceDataIncident.pending, (state) => {
      state.getIncidentLoading = "pending";
      state.incident = null;
    });
    builder.addCase(getSourceDataIncident.fulfilled, (state, { payload }) => {
      state.incident = payload;
      state.getIncidentLoading = "fulfilled";
    });
    builder.addCase(getSourceDataIncident.rejected, (state) => {
      state.getIncidentLoading = "rejected";
      state.incident = null;
    });

    // Dragonfly data reducers
    builder.addCase(getSourceDataDragonfly.pending, (state) => {
      state.getDragonflyLoading = "pending";
      state.dragonfly = null;
    });
    builder.addCase(getSourceDataDragonfly.fulfilled, (state, { payload }) => {
      state.dragonfly = payload;
      state.getDragonflyLoading = "fulfilled";
    });
    builder.addCase(getSourceDataDragonfly.rejected, (state) => {
      state.getDragonflyLoading = "rejected";
      state.dragonfly = null;
    });

    // Nearmiss data reducers
    builder.addCase(getSourceDataNearmiss.pending, (state) => {
      state.getNearmissLoading = "pending";
      state.nearmiss = null;
    });
    builder.addCase(getSourceDataNearmiss.fulfilled, (state, { payload }) => {
      state.nearmiss = payload;
      state.getNearmissLoading = "fulfilled";
    });
    builder.addCase(getSourceDataNearmiss.rejected, (state) => {
      state.getNearmissLoading = "rejected";
      state.nearmiss = null;
    });
  },
});

export const { resetSourceData } = actions;

export default reducer;
